import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { getFirebaseApp, getDb } from "./client";
import { paths, refCol } from "@/lib/db/paths";

let registrationCached: ServiceWorkerRegistration | null = null;

async function waitForActive(reg: ServiceWorkerRegistration): Promise<void> {
  if (reg.active) return;
  const sw = reg.installing ?? reg.waiting;
  if (!sw) {
    await navigator.serviceWorker.ready;
    return;
  }
  await new Promise<void>((resolve) => {
    const onChange = () => {
      if (sw.state === "activated") {
        sw.removeEventListener("statechange", onChange);
        resolve();
      }
    };
    sw.addEventListener("statechange", onChange);
  });
}

async function ensureSwRegistration(): Promise<ServiceWorkerRegistration> {
  if (registrationCached?.active) return registrationCached;
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker nieobsługiwany");
  }
  const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
    scope: "/",
  });
  await waitForActive(reg);
  registrationCached = reg;
  return reg;
}

/**
 * Stabilny doc ID dla tokena FCM — pochodna SHA-1 z full tokena (truncated do 40 hex).
 * Powód: bez deterministycznego ID każde wywołanie `enableNotifications` tworzy
 * nowy dokument (addDoc), więc po kilku odświeżeniach mamy duplikaty tego samego
 * tokena → duplicate push → szum + bałagan w fcmTokens.
 *
 * Hash zamiast samego tokena bo tokeny FCM mają "/" które są zarezerwowane
 * w Firestore path. SubtleCrypto dostępne w secure context (https + localhost).
 */
async function tokenDocId(token: string): Promise<string> {
  const data = new TextEncoder().encode(token);
  const hashBuf = await crypto.subtle.digest("SHA-1", data);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function fetchToken(): Promise<string> {
  const vapid = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapid) {
    throw new Error(
      "Brak VITE_FIREBASE_VAPID_KEY w env (Firebase → Project settings → Cloud Messaging → Web Push certificates).",
    );
  }
  const reg = await ensureSwRegistration();
  const messaging = getMessaging(getFirebaseApp());
  const token = await getToken(messaging, {
    vapidKey: vapid,
    serviceWorkerRegistration: reg,
  });
  if (!token) throw new Error("Nie udało się pobrać tokena FCM");
  return token;
}

/**
 * Upsertuje token tego użytkownika w households/{hid}/fcmTokens/{hash(token)}.
 * Idempotent: ten sam token → ten sam doc ID → setDoc nie tworzy duplikatu.
 * Stare tokeny tego usera z INNYM ID (po rotacji) czyści `cleanupStaleTokens`.
 */
async function upsertTokenDoc(hid: string, uid: string, token: string): Promise<void> {
  const db = getDb();
  const id = await tokenDocId(token);
  const ref = doc(db, ...paths.fcmTokens(hid), id);
  await setDoc(ref, {
    userId: uid,
    token,
    platform:
      typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : "",
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Usuwa stare doki FCM tego usera które NIE pasują do bieżącego tokena.
 * Bez tego po każdej rotacji tokena (browser refresh, update) zostawalibyśmy
 * z martwym dokiem aż backend wyrzuci go po registration-token-not-registered.
 * Best-effort.
 */
async function cleanupStaleTokens(hid: string, uid: string, currentToken: string): Promise<void> {
  try {
    const db = getDb();
    const currentId = await tokenDocId(currentToken);
    const snap = await getDocs(
      query(collection(db, ...paths.fcmTokens(hid)), where("userId", "==", uid)),
    );
    const stale = snap.docs.filter((d) => d.id !== currentId);
    await Promise.allSettled(stale.map((d) => deleteDoc(d.ref)));
  } catch {
    /* silent */
  }
}

/**
 * Pełny flow włączania powiadomień — wywołanie EXPLICITNE z UI (banner / Settings).
 * Pyta o uprawnienia. Bez "granted" rzuca błąd.
 */
export async function enableNotifications(
  hid: string,
  uid: string,
): Promise<{ token: string }> {
  if (!(await isSupported())) {
    throw new Error("Push nieobsługiwany w tej przeglądarce");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Odmowa uprawnień do powiadomień");
  }
  const token = await fetchToken();
  await upsertTokenDoc(hid, uid, token);
  await cleanupStaleTokens(hid, uid, token);
  return { token };
}

/**
 * Cichy re-register tokena — wywoływany na starcie aplikacji jeśli user
 * ma już granted permission. To naprawia trzy realne scenariusze:
 *
 *  1. Rotacja tokena FCM (browser może rotować co kilka dni / przy update).
 *     Stary token przestaje działać, deliverPush dostaje registration-token-
 *     not-registered i kasuje doc. Bez auto-refresh user kończy z zero
 *     tokenami i NIE dostaje pushy. To główna przyczyna "powiadomienia
 *     przestały działać po jakimś czasie".
 *  2. Permission carryover między userami na shared device. Login user A,
 *     wylogowanie czyści token, login user B — permission nadal granted
 *     ale brak tokena dla user B.
 *  3. Pierwsze entry po nowym deployu SW — token nadal valid ale chcemy
 *     mieć aktualny `updatedAt` i `platform`.
 *
 * Nie pyta o uprawnienia (silent). No-op gdy permission != granted.
 */
export async function ensureFcmRegistration(
  hid: string,
  uid: string,
): Promise<void> {
  if (typeof window === "undefined") return;
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (!(await isSupported())) return;
  try {
    const token = await fetchToken();
    await upsertTokenDoc(hid, uid, token);
    await cleanupStaleTokens(hid, uid, token);
  } catch (e) {
    console.warn("[ensureFcmRegistration] failed:", e);
  }
}

/**
 * Listener foreground messages. Wywołuje callback gdy push przyjdzie
 * podczas gdy zakładka jest aktywna (background obsługuje SW).
 */
export function listenForegroundMessages(
  cb: (msg: MessagePayload) => void,
): () => void {
  let unsub = () => {};
  void isSupported().then((ok) => {
    if (!ok) return;
    const messaging = getMessaging(getFirebaseApp());
    unsub = onMessage(messaging, cb);
  });
  return () => unsub();
}
